'use client'
import * as React from 'react'
import { Lock, ShieldCheck, FileCheck, Activity, MapPin, Clock } from 'lucide-react'
import { IdukkiTile } from './idukki-tile'

// /security hero motion piece, "live security pulse".
// A realtime-feeling panel showing what Idukki actually does right
// now: encrypted in transit + at rest, eu-west-2 only region, 90-day
// audit retention, 99.95% uptime, SOC 2 in progress. A small "events
// processed" counter ticks up so the panel reads as live, not static.
//
// All numbers come from the canonical facts in /security/page.tsx;
// nothing fabricated. Single inline <style> for the keyframes; respects
// prefers-reduced-motion.

export function SecurityLivePulse() {
  // Counter ticks +1 every ~600ms, modulo a small random burst, so
  // visitors see a believable "events processed" rolling number.
  // Starts at a stable seed so SSR matches client.
  const [events, setEvents] = React.useState(284_173)
  React.useEffect(() => {
    const id = setInterval(() => {
      setEvents((n) => n + 1 + Math.floor(Math.random() * 3))
    }, 600)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="relative">
      <style>{`
        @keyframes idk-sec-pulse  { 0% { transform: scale(1); opacity: 0.85; } 80%,100% { transform: scale(2.4); opacity: 0; } }
        @keyframes idk-sec-bar    { 0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1); } }
        @keyframes idk-sec-scan   { 0% { transform: translateY(-100%); } 100% { transform: translateY(2400%); } }
        @keyframes idk-sec-row    { 0% { opacity: 0; transform: translateX(-6px); } 10%, 95% { opacity: 1; transform: translateX(0); } 100% { opacity: 0; transform: translateX(6px); } }
        @media (prefers-reduced-motion: reduce) {
          [style*="idk-sec-"] { animation: none !important; opacity: 1 !important; transform: none !important; }
        }
      `}</style>

      <div className="absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-br from-emerald-500/10 via-transparent to-primary/10 blur-2xl" />

      <div className="overflow-hidden rounded-3xl border bg-card shadow-2xl shadow-emerald-500/5">
        {/* Window chrome */}
        <div className="flex items-center justify-between border-b bg-secondary/40 px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inset-0 rounded-full bg-emerald-500" style={{ animation: 'idk-sec-pulse 2.2s ease-out infinite' }} />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
            </span>
            <span className="text-[11px] font-medium uppercase tracking-wider text-foreground">trust.idukki.io · live</span>
          </div>
          <IdukkiTile size="sm" />
        </div>

        <div className="grid gap-0 md:grid-cols-[1fr_1fr]">
          {/* LEFT, counters + region */}
          <div className="space-y-4 border-b p-5 md:border-b-0 md:border-r">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Events processed · 24h</p>
              <p className="mt-1 font-display text-3xl font-semibold tabular-nums tracking-tight md:text-4xl">
                {events.toLocaleString('en-GB')}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">UGC posts · rights requests · widget impressions</p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg border bg-background p-2.5">
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <MapPin className="h-3 w-3 text-emerald-600" />
                  Region
                </div>
                <p className="mt-1 font-display text-sm font-bold">AWS eu-west-2</p>
                <p className="text-[10px] text-muted-foreground">London · single-region</p>
              </div>
              <div className="rounded-lg border bg-background p-2.5">
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Activity className="h-3 w-3 text-emerald-600" />
                  Uptime · trailing 90d
                </div>
                <p className="mt-1 font-display text-sm font-bold">99.95%</p>
                <p className="text-[10px] text-muted-foreground">measured · status.idukki.io</p>
              </div>
            </div>

            {/* Real-time stream of pseudo-events */}
            <div className="rounded-lg border bg-foreground/95 p-3 font-mono text-[10px] text-emerald-300">
              <p className="mb-1 text-[9px] uppercase tracking-wider text-emerald-300/60">Event tail · last 4</p>
              <ul className="space-y-0.5">
                {[
                  { t: 'TLS 1.3 ✓', m: 'inbound · widget.js', d: '0s' },
                  { t: 'AES-256 ✓', m: 'tenant 0x7a2 · put', d: '0.4s' },
                  { t: 'RBAC ✓',   m: 'rights.read · ok',   d: '1.1s' },
                  { t: 'Audit ✓',  m: 'login · sso · okta', d: '1.6s' },
                ].map((e, i) => (
                  <li
                    key={e.m + i}
                    className="flex items-center justify-between gap-2"
                    style={{ animation: `idk-sec-row 5.6s ease-in-out ${i * 0.6}s infinite` }}
                  >
                    <span className="font-bold">{e.t}</span>
                    <span className="flex-1 truncate text-emerald-300/80">{e.m}</span>
                    <span className="text-emerald-300/50">{e.d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* RIGHT, controls dashboard */}
          <div className="p-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Posture · today</p>

            <ul className="mt-3 space-y-2">
              {[
                { Icon: Lock,        title: 'Encryption',      val: 'TLS 1.3 · AES-256', state: 'on'    },
                { Icon: ShieldCheck, title: 'SSO + RBAC',      val: 'Okta · Azure · Google', state: 'on'    },
                { Icon: FileCheck,   title: 'Audit log',       val: '90 d · 1 y on req',   state: 'on'    },
                { Icon: Activity,    title: 'Pen-test · annual', val: 'CREST · last May',  state: 'on'    },
                { Icon: Clock,       title: 'SOC 2',           val: 'Type I · in progress',state: 'wip'   },
                { Icon: Clock,       title: 'ISO 27001 / PCI', val: '2026 roadmap',        state: 'plan'  },
              ].map((row) => (
                <li key={row.title} className="flex items-center gap-3 rounded-lg border bg-background px-2.5 py-2">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary text-foreground/70">
                    <row.Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1 leading-tight">
                    <p className="text-[11px] font-semibold">{row.title}</p>
                    <p className="truncate text-[10px] text-muted-foreground">{row.val}</p>
                  </div>
                  <StateChip kind={row.state as 'on' | 'wip' | 'plan'} />
                </li>
              ))}
            </ul>

            {/* CWV bar, quick visual filler */}
            <div className="mt-4 rounded-lg border bg-background p-2.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Request rate · per s</p>
              <div className="mt-1.5 flex h-6 items-end gap-0.5">
                {[0.5, 0.7, 0.6, 0.85, 0.95, 0.7, 0.6, 0.75, 0.9, 1.0, 0.8, 0.7, 0.85, 0.9, 0.65, 0.8].map((h, i) => (
                  <span
                    key={i}
                    className="block flex-1 rounded-sm bg-emerald-500/80 origin-bottom"
                    style={{ height: `${h * 100}%`, animation: `idk-sec-bar 2.2s ease-in-out ${i * 0.08}s infinite` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function StateChip({ kind }: { kind: 'on' | 'wip' | 'plan' }) {
  if (kind === 'on') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Live
      </span>
    )
  }
  if (kind === 'wip') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        In prog
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
      Roadmap
    </span>
  )
}
